<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header"><span>系统设置</span></div>
      <p class="page-tip">
        登录验证码与会员支付请分别在「短信」「微信支付/支付宝」Tab 配置
        <code>sms.*</code> / <code>pay.*</code>；门户智能体 LLM 与扣费由 Actionow 统一管理。
        邮件与 OSS 仍以 <code>actionow.</code> 开头并同步到漫剧服务。
      </p>
      <div class="settings-toolbar">
        <el-radio-group v-model="activeTab" size="small" class="settings-tabs">
          <el-radio-button
            v-for="tab in tabs"
            :key="tab.key"
            :label="tab.key"
          >{{ tab.label }}</el-radio-button>
        </el-radio-group>
        <el-input
          v-model="keyword"
          class="settings-search"
          style="width: 280px"
          placeholder="按 Key / 说明搜索"
          clearable
          prefix-icon="el-icon-search"
        />
        <span class="settings-count">共 {{ filteredList.length }} 项</span>
      </div>
      <el-table v-loading="loading" :data="filteredList" border stripe>
        <el-table-column prop="configKey" label="配置键" min-width="180" />
        <el-table-column label="配置值" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span>{{ displayValue(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="说明" min-width="200" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span>{{ displayRemark(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openEditDialog(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog title="编辑配置" :visible.sync="visible" width="520px">
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="配置键">
          <el-input v-model="form.configKey" disabled />
        </el-form-item>
        <el-form-item label="配置值" prop="configValue">
          <el-input
            v-if="isSecretKey(form.configKey)"
            v-model="form.configValue"
            type="password"
            show-password
            placeholder="请输入千问 API Key"
          />
          <el-select
            v-else-if="isSelectKey(form.configKey)"
            v-model="form.configValue"
            placeholder="请选择"
            style="width: 100%"
          >
            <el-option
              v-for="opt in getSelectOptions(form.configKey)"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-input v-else v-model="form.configValue" type="textarea" :rows="4" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="form.remark" disabled />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getSystemConfig, updateSystemConfig } from '@/api/admin'

const SECRET_KEY_PATTERN = /api[_-]?key|secret|password|token/i

const SELECT_OPTIONS = {
  'storage.type': [
    { value: 'local', label: '本地存储 (local)' },
    { value: 'oss', label: '阿里云 OSS (oss)' },
    { value: 'tos', label: '火山 TOS (tos)' }
  ],
  'sms.enabled': [
    { value: 'true', label: '启用' },
    { value: 'false', label: '停用' }
  ],
  'sms.fallback.enabled': [
    { value: 'true', label: '启用' },
    { value: 'false', label: '停用' }
  ],
  'pay.mock_enabled': [
    { value: 'true', label: '启用' },
    { value: 'false', label: '停用' }
  ],
  'pay.mobile_pay_mode': [
    { value: 'native', label: '扫码支付（手机也展示二维码）' },
    { value: 'h5', label: '手机 H5 跳转收银台' }
  ],
  'actionow.mail.enabled': [
    { value: 'true', label: '启用' },
    { value: 'false', label: '停用' }
  ]
}

const REMARK_OVERRIDES = {
  'pay.mobile_pay_mode': '手机端支付：native=扫码，h5=跳转收银台'
}

export default {
  name: 'AdminSettings',
  data() {
    return {
      loading: false,
      list: [],
      activeTab: 'all',
      keyword: '',
      visible: false,
      submitting: false,
      form: { configKey: '', configValue: '', remark: '' },
      rules: {
        configValue: [{
          validator: (rule, value, callback) => {
            if (this.isSecretKey(this.form.configKey)) {
              callback()
              return
            }
            if (this.isSelectKey(this.form.configKey)) {
              if (!value && value !== 'false') {
                callback(new Error('请选择配置值'))
                return
              }
              callback()
              return
            }
            if (!value || !String(value).trim()) {
              callback(new Error('请输入配置值'))
              return
            }
            callback()
          },
          trigger: 'change'
        }]
      }
    }
  },
  computed: {
    tabs() {
      return [
        { key: 'all', label: '全部' },
        { key: 'builtin', label: '平台内置' },
        { key: 'sms', label: '短信' },
        { key: 'mail', label: '邮件' },
        { key: 'pay_wechat', label: '微信支付' },
        { key: 'pay_alipay', label: '支付宝' },
        { key: 'oss', label: '存储/OSS' }
      ]
    },
    filteredList() {
      let rows = (this.list || []).filter(item => !String(item.configKey || '').startsWith('pay.stripe.'))
      const tab = this.activeTab || 'all'
      if (tab !== 'all') {
        rows = rows.filter(item => {
          const key = item.configKey || ''
          if (tab === 'builtin') return !key.startsWith('actionow.') && !key.startsWith('sms.') && !key.startsWith('pay.')
          if (tab === 'sms') return key.startsWith('sms.')
          if (tab === 'mail') return key.startsWith('actionow.mail.')
          if (tab === 'pay_wechat') {
            return key.startsWith('pay.wechat.')
              || key === 'pay.mobile_pay_mode'
              || key === 'pay.mock_enabled'
              || key === 'pay.portal_base_url'
          }
          if (tab === 'pay_alipay') {
            return key.startsWith('pay.alipay.')
              || key === 'pay.mobile_pay_mode'
              || key === 'pay.mock_enabled'
              || key === 'pay.portal_base_url'
          }
          if (tab === 'oss') return key.startsWith('actionow.oss.')
          return true
        })
      }
      const keyword = (this.keyword || '').trim().toLowerCase()
      if (keyword) {
        rows = rows.filter(item =>
          String(item.configKey || '').toLowerCase().includes(keyword)
          || String(item.remark || '').toLowerCase().includes(keyword)
          || String(item.configValue || '').toLowerCase().includes(keyword)
        )
      }
      return rows
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    isSecretKey(key) {
      return SECRET_KEY_PATTERN.test(key || '')
    },
    isSelectKey(key) {
      return Object.prototype.hasOwnProperty.call(SELECT_OPTIONS, key)
    },
    getSelectOptions(key) {
      return SELECT_OPTIONS[key] || []
    },
    resolveSelectLabel(key, value) {
      const options = this.getSelectOptions(key)
      const hit = options.find(opt => opt.value === String(value))
      return hit ? hit.label : value
    },
    displayValue(row) {
      const val = row.configValue
      if (val == null || val === '') return '—'
      if (this.isSecretKey(row.configKey)) {
        if (val.length <= 8) return '********'
        return val.slice(0, 4) + '****' + val.slice(-4)
      }
      if (this.isSelectKey(row.configKey)) {
        return this.resolveSelectLabel(row.configKey, val)
      }
      return val
    },
    displayRemark(row) {
      const key = row.configKey || ''
      if (Object.prototype.hasOwnProperty.call(REMARK_OVERRIDES, key)) {
        return REMARK_OVERRIDES[key]
      }
      return row.remark || '—'
    },
    async loadConfig() {
      this.loading = true
      try {
        this.list = await getSystemConfig() || []
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openEditDialog(row) {
      let configValue = row.configValue == null ? '' : String(row.configValue)
      if (this.isSelectKey(row.configKey)) {
        const options = this.getSelectOptions(row.configKey)
        const allowed = options.map(opt => opt.value)
        if (!allowed.includes(configValue)) {
          configValue = allowed[0] || ''
        }
      }
      this.form = {
        ...row,
        configValue,
        remark: this.displayRemark(row)
      }
      this.visible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          await updateSystemConfig({
            configKey: this.form.configKey,
            configValue: this.form.configValue == null ? '' : String(this.form.configValue).trim(),
            remark: this.form.remark
          })
          this.$message.success('保存成功')
          this.visible = false
          this.loadConfig()
        } catch (e) {
        } finally {
          this.submitting = false
        }
      })
    }
  }
}
</script>

<style scoped>
.page-tip {
  margin: 0 0 16px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.page-tip code {
  padding: 2px 6px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
}
.page-tip a {
  color: #409eff;
}
.settings-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 0 0 16px;
}
.settings-tabs {
  margin-right: 8px;
}
.settings-count {
  margin-left: auto;
  color: #909399;
  font-size: 13px;
}
.settings-search {
  max-width: 280px;
}
</style>
